/**
 * Emoji Decoder — shared types.
 *
 * Active round lives in Redis (chaos-style hot path), with a separate
 * SET NX claim sentinel for race-free win attribution. DB rows flush at
 * round end.
 */

export const DIFFICULTIES = ['easy', 'medium', 'hard', 'mixed'] as const;
export type Difficulty = (typeof DIFFICULTIES)[number];

export const CATEGORIES = [
  'mixed',
  'movies',
  'songs',
  'tv_shows',
  'video_games',
  'phrases',
  'celebs',
] as const;
export type Category = (typeof CATEGORIES)[number];

export const CATEGORY_LABEL: Readonly<Record<Category, string>> = {
  mixed: 'Mixed',
  movies: 'Movies',
  songs: 'Songs',
  tv_shows: 'TV Shows',
  video_games: 'Video Games',
  phrases: 'Phrases',
  celebs: 'Celebrities',
};

/** Hint emoji rendered next to the category label. */
export const CATEGORY_EMOJI: Readonly<Record<Category, string>> = {
  mixed: '✨',
  movies: '🎬',
  songs: '🎵',
  tv_shows: '📺',
  video_games: '🎮',
  phrases: '💬',
  celebs: '⭐',
};

/** Background tint shift per category. Used by the overlay to set
 *  --ed-tint without inlining a color picker in the runtime config. */
export const CATEGORY_TINT: Readonly<Record<Category, string>> = {
  mixed: '#9333ea',
  movies: '#7c3aed',
  songs: '#e879f9',
  tv_shows: '#3b82f6',
  video_games: '#10b981',
  phrases: '#f97316',
  celebs: '#f59e0b',
};

export interface EmojiDecoderSettings {
  gemReward: number;
  roundCooldownSeconds: number;
  roundTimeoutSeconds: number;
  difficulty: Difficulty;
  category: Category;
  hintDelaySeconds: number;
  autoAdvance: boolean;
  showFunFact: boolean;
  /** Per-difficulty multipliers (percent). 100 = no change. Applied on payout. */
  easyMultiplier: number;
  mediumMultiplier: number;
  hardMultiplier: number;
  /** Max rounds per session before auto-stop. 0 = unlimited. */
  maxRoundsPerSession: number;
  /** Chat prefix override. NULL = default. */
  chatPrefix: string | null;
}

export const DEFAULT_SETTINGS: EmojiDecoderSettings = {
  gemReward: 200,
  roundCooldownSeconds: 10,
  roundTimeoutSeconds: 60,
  difficulty: 'mixed',
  category: 'mixed',
  hintDelaySeconds: 0,
  autoAdvance: true,
  showFunFact: true,
  easyMultiplier: 100,
  mediumMultiplier: 100,
  hardMultiplier: 100,
  maxRoundsPerSession: 0,
  chatPrefix: null,
};

/**
 * Server-side round state. The overlay snapshot strips `answer` and
 * `alternateAnswers` so chat can't inspect the page source for the
 * solution.
 */
export interface RoundState {
  roundId: string;
  streamerId: string;
  roundNumber: number;
  emojis: string;
  answer: string;
  alternateAnswers: string[];
  hint: string;
  category: Category;
  difficulty: Exclude<Difficulty, 'mixed'>;
  funFact: string | null;
  startedAt: number;
  /** Round auto-skips at this UTC ms if nobody answers. */
  timeoutAt: number;
  /** Set when first correct answer claims via SET NX. */
  solved: boolean;
  winnerUserId: string | null;
  winnerUsername: string | null;
  winnerPlatform: 'twitch' | 'youtube' | 'kick' | 'hypevault' | null;
  winnerPlatformUserId: string | null;
  solvedAt: number | null;
  gemReward: number;
  hintRevealAt: number;
}

/** What the overlay sees — answer + alternates redacted. */
export interface RoundSnapshot {
  roundId: string;
  roundNumber: number;
  emojis: string;
  hint: string;
  category: Category;
  difficulty: Exclude<Difficulty, 'mixed'>;
  startedAt: number;
  timeoutAt: number;
  hintRevealAt: number;
  /** Phase: 'guessing' = live, 'solved' = win, 'timeout' = nobody got it,
   *  'cooldown' = between rounds. */
  phase: 'guessing' | 'solved' | 'timeout' | 'cooldown';
  /** When set, render the winner takeover. */
  winner: {
    username: string;
    answer: string;
    gemReward: number;
    funFact: string | null;
  } | null;
  /** When set (timeout), reveal the unsolved answer. */
  reveal: { answer: string; funFact: string | null } | null;
  /** When set (auto-advance cooldown), countdown end UTC ms. */
  cooldownEndsAt: number | null;
  /** Most recent winners feed (last 5). */
  recentWinners: Array<{
    username: string;
    emojis: string;
    answer: string;
    gemReward: number;
    solvedAt: number;
  }>;
  gemReward: number;
}

export interface ChatGuess {
  streamerId: string;
  viewerName: string;
  viewerPlatformId: string | null;
  platform: 'twitch' | 'youtube' | 'kick' | 'hypevault';
  message: string;
}
